import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { stripePromise } from '../stripe';

const API_BASE = process.env.REACT_APP_API_URL || '';
const LOGO = '/dailyreps3.png';

export default function ParentActivate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [completing, setCompleting] = useState(!!sessionId);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');

  // Affiliate tracking
  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=1';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  // Affiliate tracking (sale confirmation)
  useEffect(() => {
    if (!sessionId) return;
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=3';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, [sessionId]);

  // Fetch checkout client secret (only when showing the form, not on return)
  useEffect(() => {
    if (sessionId) return;
    const t = localStorage.getItem('trialxyz');
    const body = t ? JSON.stringify({ trial_days: parseInt(t, 10) }) : undefined;
    fetch(`${API_BASE}/api/parent/activate-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body,
    })
      .then(r => r.ok ? r.json() : r.json().catch(() => ({})).then(d => Promise.reject(new Error(d.error || 'Failed to start checkout'))))
      .then(data => setClientSecret(data.client_secret))
      .catch(err => {
        const msg = err?.message || 'Failed to start checkout';
        if (msg.includes('already be activated') || msg.includes('No pending activation')) {
          navigate('/parent', { replace: true });
        } else {
          setError(msg);
        }
      });
  }, [sessionId, token, navigate]);

  // If returning from Stripe with session_id, complete activation
  useEffect(() => {
    if (!sessionId) return;
    setCompleting(true);
    fetch(`${API_BASE}/api/parent/activate-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(new Error(d.error || 'Activation failed'))))
      .then(() => { localStorage.removeItem('trialxyz'); navigate('/parent'); })
      .catch(err => {
        setError(err.message);
        setCompleting(false);
      });
  }, [sessionId, token, navigate]);

  if (completing) {
    return (
      <div className="order-page">
        <header className="order-header"><div className="order-wrap">
          <div className="order-logo-row">
            <img src={LOGO} alt="Train Daily" className="order-logo" />
            <span className="order-brand">Train Daily</span>
          </div>
        </div></header>
        <main className="order-main"><div className="order-wrap" style={{ textAlign: 'center', padding: '60px 20px' }}>
          {error ? (
            <div className="order-error-card"><h2>Activation Error</h2><p>{error}</p></div>
          ) : (
            <>
              <div className="spinner" style={{ margin: '0 auto 20px' }} />
              <p>Completing your registration...</p>
            </>
          )}
        </div></main>
      </div>
    );
  }

  return (
    <div className="order-page">
      <header className="order-header"><div className="order-wrap">
        <div className="order-logo-row">
          <img src={LOGO} alt="Train Daily" className="order-logo" />
          <span className="order-brand">Train Daily</span>
        </div>
      </div></header>
      <main className="order-main"><div className="order-wrap">
        <h1 className="order-title" style={{ marginBottom: 8 }}>Complete Your Registration</h1>
        <p className="order-lead">Start your 30-day free trial. Cancel anytime before day 30 and pay nothing.</p>
        {error && <div className="order-error" style={{ marginBottom: 16 }}>{error}</div>}
        {clientSecret ? (
          <div className="order-checkout-embed">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        ) : !error && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        )}
      </div></main>
    </div>
  );
}
